import React, { useEffect, useRef } from 'react';

/** Approximate plant coordinates in Tamil Nadu. */
export const PLANT_LOCATIONS = [
  { name: 'Manali', area: 'Chennai North', lat: 13.1667, lng: 80.2667 },
  { name: 'Periyapalayam', area: 'Tiruvallur', lat: 13.35, lng: 80.05 },
  { name: 'Medavakkam', area: 'Chennai South', lat: 12.917, lng: 80.191 },
  { name: 'Nagapattinam', area: 'Nagapattinam', lat: 10.7672, lng: 79.8449 },
  { name: 'Oragadam', area: 'Kanchipuram', lat: 12.83, lng: 79.95 },
];

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const TN_CENTER = [11.1271, 78.6569];
const TN_ZOOM = 6.4;

const PIN_SVG = `
  <svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true" focusable="false">
    <path
      d="M12 1.5C7.3 1.5 3.5 5.3 3.5 10c0 6.4 7.2 14.2 8.1 15.1a0.7 0.7 0 0 0 1 0C13.4 24.2 20.5 16.4 20.5 10 20.5 5.3 16.7 1.5 12 1.5z"
      fill="currentColor"
      stroke="#061228"
      stroke-width="1.2"
    />
    <circle cx="12" cy="10" r="3.4" fill="#ffffff" />
  </svg>
`;

function ensureLeafletAssets() {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L));
      existing.addEventListener('error', reject);
      if (window.L) resolve(window.L);
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function createPinIcon(L, active = false) {
  return L.divIcon({
    className: `login-plant-pin${active ? ' is-active' : ''}`,
    html: PIN_SVG,
    iconSize: active ? [30, 40] : [26, 34],
    iconAnchor: active ? [15, 40] : [13, 34],
    tooltipAnchor: [0, -36],
  });
}

function LoginPlantMap({
  locations = PLANT_LOCATIONS,
  activeName = '',
  onSelect,
}) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let cancelled = false;

    ensureLeafletAssets()
      .then((L) => {
        if (cancelled || !mapNodeRef.current || mapRef.current) return;

        const map = L.map(mapNodeRef.current, {
          center: TN_CENTER,
          zoom: TN_ZOOM,
          zoomControl: false,
          attributionControl: true,
          scrollWheelZoom: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 18,
          attribution: '&copy; OpenStreetMap',
        }).addTo(map);

        L.control.zoom({ position: 'bottomright' }).addTo(map);

        const markers = {};
        locations.forEach((plant) => {
          const marker = L.marker([plant.lat, plant.lng], {
            icon: createPinIcon(L, false),
            riseOnHover: true,
          })
            .addTo(map)
            .bindTooltip(plant.name, {
              direction: 'top',
              offset: [0, -8],
              opacity: 0.95,
            });

          marker.on('click', () => {
            onSelectRef.current?.(plant.name);
          });

          markers[plant.name] = marker;
        });

        markersRef.current = markers;
        mapRef.current = map;

        window.setTimeout(() => {
          map.invalidateSize();
        }, 80);
      })
      .catch(() => {
        // Keep empty container if CDN load fails.
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markersRef.current = {};
      }
    };
  }, [locations]);

  useEffect(() => {
    const L = window.L;
    const map = mapRef.current;
    const markers = markersRef.current;
    if (!L || !map || !markers) return;

    Object.entries(markers).forEach(([name, marker]) => {
      const active = name === activeName;
      marker.setIcon(createPinIcon(L, active));
      if (active) {
        marker.setZIndexOffset(600);
        marker.openTooltip();
        map.panTo(marker.getLatLng(), { animate: true });
      } else {
        marker.setZIndexOffset(0);
        marker.closeTooltip();
      }
    });
  }, [activeName]);

  return (
    <div className="login-plant-map" aria-label="Tamil Nadu plant locations map">
      <div ref={mapNodeRef} className="login-plant-map__canvas" />
      <div className="login-plant-map__badge">Tamil Nadu</div>
    </div>
  );
}

export default LoginPlantMap;
